import { t } from "@lingui/macro";
import type { DailyCallStats } from "@repo/common";
import { Filter, TrendingUp } from "lucide-react";
import React, { useMemo } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { last30Days } from "../utils/dashboardStats";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";

interface CallActivityGraphProps {
	dailyStats: DailyCallStats[];
	language: "en" | "he";
}

/** Returns a YYYY-MM-DD string for the local calendar date `offset` days ago. */
function localDateKeyOffset(daysAgo: number): string {
	const d = new Date();
	d.setDate(d.getDate() - daysAgo);
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

export function CallActivityGraph({
	dailyStats,
	language,
}: CallActivityGraphProps) {
	const [showBlockedCalls, setShowBlockedCalls] = React.useState(true);
	const [showTestCalls, setShowTestCalls] = React.useState(true);

	const days = 30;
	const { start: cutoff, end } = last30Days();

	const chartData = useMemo(() => {
		// Build a map of date -> call count from dailyStats docs
		const countByDate: Record<string, number> = {};

		for (const doc of dailyStats) {
			if (doc.dateKey < cutoff || doc.dateKey > end) continue;

			// Which segments to include
			let count = 0;
			const seg = doc.totals.segments;

			if (showBlockedCalls && showTestCalls) {
				count = doc.totals.overall.callCount;
			} else if (!showBlockedCalls && !showTestCalls) {
				count = seg.normal.callCount;
			} else if (!showBlockedCalls) {
				// hide blocked: normal + testOnly
				count = seg.normal.callCount + seg.testOnly.callCount;
			} else {
				// hide test: normal + blockedOnly
				count = seg.normal.callCount + seg.blockedOnly.callCount;
			}

			countByDate[doc.dateKey] = (countByDate[doc.dateKey] ?? 0) + count;
		}

		// Build ordered array for all dates in window (oldest → newest)
		const result: { date: string; displayDate: string; calls: number }[] = [];
		for (let i = days - 1; i >= 0; i--) {
			const key = localDateKeyOffset(i);

			const displayDate = new Date(key + "T12:00:00").toLocaleDateString(
				language,
				{ month: "numeric", day: "numeric" },
			);
			result.push({ date: key, displayDate, calls: countByDate[key] ?? 0 });
		}
		return result;
	}, [dailyStats, cutoff, end, showBlockedCalls, showTestCalls, language]);

	// Show a date label every five days within the fixed 30-day window.
	const tickInterval = 4;

	return (
		<Card>
			<CardHeader
				dir={language === "he" ? "rtl" : "ltr"}
				className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 border-b border-card-border pb-4"
			>
				<div>
					<CardTitle className="flex items-center gap-2">
						<TrendingUp className="w-5 h-5" />
						<span>{t({ id: "callActivity", message: "Call Activity" })}</span>
					</CardTitle>
					<CardDescription>
						{t({
							id: "dashboard.dailyVolume30Days",
							message: "Daily call volume · Last 30 days",
						})}
					</CardDescription>
				</div>
				<div className="flex items-center gap-2">
					{/* Filter dropdown */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								size="sm"
								className="flex items-center gap-2"
								aria-label={t({ id: "filterCalls", message: "Filters" })}
							>
								<Filter className="w-4 h-4" />
								<span>{t({ id: "filterCalls", message: "Filters" })}</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56">
							<DropdownMenuCheckboxItem
								checked={showTestCalls}
								onCheckedChange={setShowTestCalls}
							>
								{t({ id: "showTestCalls", message: "Show Test Calls" })}
							</DropdownMenuCheckboxItem>
							<DropdownMenuCheckboxItem
								checked={showBlockedCalls}
								onCheckedChange={setShowBlockedCalls}
							>
								{t({ id: "showBlockedCalls", message: "Show Blocked Calls" })}
							</DropdownMenuCheckboxItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</CardHeader>
			<CardContent>
				<div
					className="rounded-md border border-card-border bg-input-background px-2 pb-2 pt-4"
					dir="ltr"
				>
					<ResponsiveContainer width="100%" height={200}>
						<BarChart data={chartData}>
							<CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
							<XAxis
								dataKey="displayDate"
								tick={{ fontSize: 12 }}
								stroke="var(--muted-foreground)"
								interval={tickInterval}
							/>
							<YAxis
								tick={{ fontSize: 12 }}
								stroke="var(--muted-foreground)"
								allowDecimals={false}
							/>
							<Tooltip
								contentStyle={{
									backgroundColor: "var(--popover)",
									border: "1px solid var(--border)",
									color: "var(--popover-foreground)",
									borderRadius: "8px",
									fontSize: "14px",
								}}
								labelStyle={{ fontWeight: 500 }}
							/>
							<Bar
								dataKey="calls"
								fill="var(--chart-1)"
								radius={[4, 4, 0, 0]}
								name={t({ id: "calls_label", message: "Calls" })}
							/>
						</BarChart>
					</ResponsiveContainer>
				</div>
			</CardContent>
		</Card>
	);
}
